import { Calculate as TaxCalculate } from './taxcalculator.mjs';

let PRICE_DATA_CACHE = null;
let METROTAX_CACHE = null;
let PENDLER20_CACHE = null;

export const TAX_TYPE_NO_TOP = 1;
export const TAX_TYPE_MELLEM = 2;
export const TAX_TYPE_TOP = 3;
export const TAX_TYPE_TOPTOP = 4;

class CompanyCalculator {
	constructor({ travelsPerMonth = 0, monthUse = 0, zones = 2, metro = false, taxType = TAX_TYPE_NO_TOP, car = null }) {
		this.travelsPerMonth = travelsPerMonth;
		this.monthUse = monthUse;
		this.zones = zones;
		this.metro = metro ? true : false;
		this.taxType = taxType;
		this.selectedCar = car;
		this._pricedata = null;
		this._metroTax = null;
		this._pendler20countData = null;
	}

	getTravlesPrYear() {
		return this.travelsPerMonth * this.monthUse;
	}

	getTravlesPrWeek() {
		return this.travelsPerMonth / 8;
	}

	pendlerkortPrice() {
		let pricePerMonth = this._pricedata['pendlerkort']['price_per_month'];
		if (this.metro) {
			pricePerMonth += this._metroTax['pendlerkort'];
		}

		let pricePrJourney = pricePerMonth / this.travelsPerMonth;
		let pricePrYear = pricePerMonth * this.monthUse;
		let pricePrTravel = pricePrYear / this.getTravlesPrYear();

		return { pricePerMonth, pricePrJourney, pricePrYear, pricePrTravel, monthUsePrYear: this.monthUse };
	}

	pendler20() {
		let price = this._pricedata['pendler20']['price'];
		if (this.metro) {
			price += this._metroTax['pendler20'];
		}

		let cardCountMatrix = this._pendler20countData[this.monthUse] ? this._pendler20countData[this.monthUse] : null;
		if (!cardCountMatrix) {
			return null;
		}

		let cardCount = cardCountMatrix[this.getTravlesPrWeek() - 1]
			? cardCountMatrix[this.getTravlesPrWeek() - 1]
			: null;
		if (!cardCount) {
			return null;
		}

		let pricePrYear = price * cardCount;
		let pricePerMonth = price / 2;
		let pricePrJourney = pricePerMonth / this.travelsPerMonth;
		let pricePrTravel = pricePrYear / this.getTravlesPrYear();

		return {
			price,
			pricePerMonth,
			pricePrJourney,
			pricePrYear,
			monthUsePrYear: this.monthUse,
			pricePrTravel,
			cardCount
		};
	}

	erhverskort({ travelKm = 0 }) {
		let price = this._pricedata['erhvervskort']['price'];
		if (this.metro) {
			price += this._metroTax['erhvervskort'];
		}

		let pricePerMonth = price / 12;
		let taxCalculations = TaxCalculate({
			monthcardPrice: this.pendlerkortPrice().pricePerMonth,
			companyCardPrice: price,
			travelKm
		});

		let montlyTaxSavings = 0;
		switch(this.taxType) {
			case TAX_TYPE_MELLEM:
				montlyTaxSavings = taxCalculations.MaanedliggBesparelseMedMellme();
				break;
			case TAX_TYPE_TOP:
				montlyTaxSavings = taxCalculations.MaanedligBesparelseMedTop();
				break;
			case TAX_TYPE_TOPTOP:
				montlyTaxSavings = taxCalculations.MaanedligBesparelseMedTopTop();
				break;
			case TAX_TYPE_NO_TOP:
			default:
				montlyTaxSavings = taxCalculations.MaanedliggBesparelseUdenTop();
				break;
		}

		let pricePerMonthIncludingTaxSavings = pricePerMonth - montlyTaxSavings;
		let pricePrJourney = pricePerMonthIncludingTaxSavings / this.travelsPerMonth;
		let monthUsePrYear = 12;
		let pricePrYear = pricePerMonthIncludingTaxSavings * monthUsePrYear;
		let pricePrTravel = pricePrYear / this.getTravlesPrYear();

		return {
			price,
			pricePerMonth,
			pricePerMonthIncludingTaxSavings,
			pricePrJourney,
			monthUsePrYear,
			pricePrYear,
			pricePrTravel,
			montlyTaxSavings
		};
	}

	rejsekort() {
		let pricePrJourney = this._pricedata['rejsekort']['price_pr_travel'];
		if (this.metro) {
			pricePrJourney += this._metroTax['rejsekort'];
		}

		let pricePrYear = this.getTravlesPrYear() * pricePrJourney;
		let pricePrTravel = pricePrYear / this.getTravlesPrYear();

		return {
			pricePrJourney,
			pricePrYear,
			pricePrTravel,
			monthUsePrYear: this.monthUse
		};
	}

	getPriceData() {
		return new Promise((resolve, reject) => {
			let zones = this.zones.toString();
			if (PRICE_DATA_CACHE && PRICE_DATA_CACHE[zones]) {
				this._pricedata = PRICE_DATA_CACHE[zones];
				resolve(this._pricedata);
			} else {
				import('./../data/prices/prices.mjs')
					.then(({ default: prices }) => {
						PRICE_DATA_CACHE = prices;
						if (prices[zones]) {
							this._pricedata = prices[zones];
							resolve(this._pricedata);
						} else {
							reject('no_zone_found');
						}
					})
					.catch((err) => {
						console.error(err);
						reject(err);
					});
			}
		});
	}

	getMetroTax() {
		return new Promise((resolve, reject) => {
			if (METROTAX_CACHE) {
				this._metroTax = METROTAX_CACHE;
				resolve(this._metroTax);
			} else {
				import('./../data/prices/metrotax.mjs')
					.then(({ default: taxes }) => {
						PRICE_DATA_CACHE = taxes;
						this._metroTax = taxes;
						resolve(this._metroTax);
					})
					.catch((err) => {
						console.error(err);
						reject(err);
					});
			}
		});
	}

	getPendler20Count() {
		return new Promise((resolve, reject) => {
			if (PENDLER20_CACHE) {
				this._pendler20countData = PENDLER20_CACHE;
				resolve(this._pendler20countData);
			} else {
				import('./../data/prices/pendler20.mjs')
					.then(({ default: pendler20data }) => {
						PENDLER20_CACHE = pendler20data;
						this._pendler20countData = pendler20data;
						resolve(this._pendler20countData);
					})
					.catch((err) => {
						console.error(err);
						reject(err);
					});
			}
		});
	}

	car({ travelKm }) {
		if (!this.selectedCar) {
			return null;
		}

		let price = this.selectedCar.kmprice;
		let pricePrJourney = price * travelKm;
		let pricePerMonth = pricePrJourney * this.travelsPerMonth;
		let pricePrYear = pricePrJourney * this.getTravlesPrYear();
		let pricePrTravel = pricePrYear / this.getTravlesPrYear();
		let priceDetails = Object.assign({}, this.selectedCar);
		let tripVarPricePrJouney = this.selectedCar.variable_price * travelKm;
		let tripVarPricePrYear = tripVarPricePrJouney * this.getTravlesPrYear();

		return {
			price,
			pricePerMonth,
			pricePrJourney,
			pricePrYear,
			monthUsePrYear: this.monthUse,
			pricePrTravel,
			priceDetails,
			tripVarPricePrJouney,
			tripVarPricePrYear
		};
	}

	loadData() {
		return Promise.all([ this.getPriceData(), this.getMetroTax(), this.getPendler20Count() ]);
	}
}

export default CompanyCalculator;
